import React, { useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Edit, Copy, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Switch,
  Button,
} from '@wove/web-ui'
import { ValidationRule } from '@wove/api'
import { cn } from '@wove/web-ui'
import { toast } from 'sonner'

// Component to display conditions in a readable format
function ConditionsSummary({ conditions }: { conditions: any }) {
  if (!conditions) return <div className="text-gray-500">No conditions defined</div>

  const renderCondition = (condition: any, depth = 0) => {
    if (condition.conditions) {
      // It's a group
      return (
        <div
          key={Math.random()}
          className={cn('space-y-2', depth > 0 && 'ml-4 pl-4 border-l-2 border-gray-200')}
        >
          <div className="flex items-center gap-2">
            <Badge
              variant={condition.operator === 'AND' ? 'default' : 'secondary'}
              className="text-xs"
            >
              {condition.operator}
            </Badge>
            <span className="text-sm text-gray-600">
              {condition.operator === 'AND' ? 'All of:' : 'Any of:'}
            </span>
          </div>
          <div className="space-y-1">
            {condition.conditions.map((c: any, i: number) => renderCondition(c, depth + 1))}
          </div>
        </div>
      )
    } else {
      // It's a single condition
      const isCustomField = condition.fieldPath?.startsWith('customFields.')
      const displayPath = isCustomField ? `${condition.fieldPath} (custom)` : condition.fieldPath

      return (
        <div key={Math.random()} className="flex items-center gap-2 text-sm">
          <span className="font-medium">{displayPath}</span>
          <span className="text-gray-600">{condition.operator}</span>
          {condition.value !== undefined && (
            <span className="text-blue-600">
              {Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}
            </span>
          )}
        </div>
      )
    }
  }

  return <div className="space-y-2">{renderCondition(conditions)}</div>
}

// Component to display requirements in a readable format
function RequirementsSummary({ requirements }: { requirements: any[] }) {
  if (!requirements || requirements.length === 0) {
    return <div className="text-gray-500">No requirements defined</div>
  }

  const groupedRequirements = requirements.reduce((acc, req) => {
    if (!acc[req.requirementType]) acc[req.requirementType] = []
    acc[req.requirementType].push(req)
    return acc
  }, {} as Record<string, any[]>)

  return (
    <div className="space-y-3">
      {Object.entries(groupedRequirements as Record<string, any[]>).map(([type, reqs]) => (
        <div key={type}>
          <div className="flex items-center gap-2 mb-1">
            <Badge
              variant={
                type === 'required' ? 'destructive' : type === 'optional' ? 'secondary' : 'outline'
              }
              className="text-xs"
            >
              {type}
            </Badge>
            <span className="text-sm text-gray-600">({reqs.length} fields)</span>
          </div>
          <div className="space-y-1 ml-2">
            {reqs.map((req, i) => {
              const isCustomField = req.fieldPath?.startsWith('customFields.')
              const displayPath = isCustomField ? `${req.fieldPath} (custom)` : req.fieldPath

              return (
                <div key={i} className="text-sm">
                  <span className="font-medium">{displayPath}</span>
                  <Badge variant="outline" className="ml-2 text-xs">
                    {req.context}
                  </Badge>
                  {req.errorMessage && (
                    <div className="text-xs text-gray-500 mt-0.5">{req.errorMessage}</div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

interface ValidationRulesTableProps {
  rules: ValidationRule[]
  onEdit: (rule: ValidationRule) => void
  onDelete: (id: string) => void
  onDuplicate: (rule: ValidationRule) => void
  onReorder: (rules: ValidationRule[]) => void
}

interface SortableRowProps {
  rule: ValidationRule
  onEdit: (rule: ValidationRule) => void
  onDelete: (id: string) => void
  onDuplicate: (rule: ValidationRule) => void
  onToggleActive: (id: string, isActive: boolean) => void
  expanded: boolean
  onToggleExpand: () => void
}

function SortableRow({
  rule,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleActive,
  expanded,
  onToggleExpand,
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        className={cn('hover:bg-gray-50', isDragging && 'opacity-50')}
      >
        <TableCell className="w-8">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
          >
            <GripVertical className="h-4 w-4 text-gray-400" />
          </button>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <button onClick={onToggleExpand} className="p-1 hover:bg-gray-100 rounded">
              {expanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            <div>
              <div className="font-medium">{rule.name}</div>
              {rule.description && <div className="text-sm text-gray-500">{rule.description}</div>}
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={rule.is_active ? 'success' : 'secondary'}>
            {rule.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </TableCell>
        <TableCell className="text-center">{rule.priority}</TableCell>
        <TableCell>
          {rule.conditions && (
            <div className="text-sm text-gray-600">
              {(rule.conditions as any).operator} (
              {(rule.conditions as any).conditions?.length || 0} conditions)
            </div>
          )}
        </TableCell>
        <TableCell>{rule.requirements?.length || 0} requirements</TableCell>
        <TableCell>
          <Switch
            checked={rule.is_active}
            onCheckedChange={(checked) => onToggleActive(rule.id, checked)}
          />
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => onEdit(rule)} className="h-8 w-8">
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDuplicate(rule)}
              className="h-8 w-8"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(rule.id)}
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={8} className="bg-gray-50">
            <div className="py-4 px-8">
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h4 className="font-medium mb-3">Conditions</h4>
                  <ConditionsSummary conditions={rule.conditions} />
                </div>
                <div>
                  <h4 className="font-medium mb-3">Requirements</h4>
                  <RequirementsSummary requirements={rule.requirements} />
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export function ValidationRulesTable({
  rules,
  onEdit,
  onDelete,
  onDuplicate,
  onReorder,
}: ValidationRulesTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [localRules, setLocalRules] = useState(rules)

  React.useEffect(() => {
    setLocalRules(rules)
  }, [rules])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = localRules.findIndex((rule) => rule.id === active.id)
      const newIndex = localRules.findIndex((rule) => rule.id === over?.id)

      const newRules = [...localRules]
      const [movedRule] = newRules.splice(oldIndex, 1)
      newRules.splice(newIndex, 0, movedRule)

      setLocalRules(newRules)
      onReorder(newRules)
    }
  }

  const toggleExpanded = (ruleId: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(ruleId)) {
      newExpanded.delete(ruleId)
    } else {
      newExpanded.add(ruleId)
    }
    setExpandedRows(newExpanded)
  }

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1'
        }/validation-rules/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ is_active: isActive }),
        },
      )

      if (!response.ok) throw new Error('Failed to update rule')

      // Update local state
      setLocalRules(
        localRules.map((rule) => (rule.id === id ? { ...rule, is_active: isActive } : rule)),
      )
    } catch (error) {
      toast.error('Failed to toggle rule status')
    }
  }

  if (localRules.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <p className="text-gray-500 mb-4">No validation rules found</p>
        <p className="text-sm text-gray-400">Create your first rule to get started</p>
      </div>
    )
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={localRules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Priority</TableHead>
              <TableHead>Conditions</TableHead>
              <TableHead>Requirements</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {localRules.map((rule) => (
              <SortableRow
                key={rule.id}
                rule={rule}
                onEdit={onEdit}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onToggleActive={handleToggleActive}
                expanded={expandedRows.has(rule.id)}
                onToggleExpand={() => toggleExpanded(rule.id)}
              />
            ))}
          </TableBody>
        </Table>
      </SortableContext>
    </DndContext>
  )
}
